import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PanelLayout from "@/features/reader/side-panel/PanelLayout";
import { useApi } from "@/hooks/use-api";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { useCurrentView } from "@/hooks/use-current-view";
import { useEffect, useState } from "react";
import { BarChart3, MessageSquareText } from "lucide-react";
import { UserRemarksTab } from "./UserRemarksTab";
import { GradeDistributionTab } from "./GradeDistributionTab";

type GradingPanelView = "remarks" | "distribution";

const TITLE_COPY: Record<
  GradingPanelView,
  { title: string; description: string }
> = {
  remarks: {
    title: "User remarks",
    description: "View grading remarks",
  },
  distribution: {
    title: "Grade distribution",
    description: "View distribution of grades",
  },
};

export const GradingPanel = () => {
  const gradingPanel = useAppState((state) => state.gradingPanel);
  const currentDocument = useAppState((state) => state.currentDocument);
  const dispatch = useAppDispatch();
  const currentView = useCurrentView();
  const { fetchGradeDistribution } = useApi();

  const gradeDistribution = useAppState((state) => state.gradeDistribution);
  const grading = useAppState((state) => state.grading);
  const [isDistributionLoading, setIsDistributionLoading] = useState(false);

  useEffect(() => {
    if (
      !gradingPanel.visible ||
      gradingPanel.view !== "distribution" ||
      !currentDocument?.id ||
      !currentDocument?.assessmentId
    ) {
      return;
    }

    if (gradeDistribution) {
      return;
    }

    let isMounted = true;
    setIsDistributionLoading(true);

    fetchGradeDistribution(currentDocument.assessmentId)
      .then((response) => {
        if (isMounted) {
          dispatch({
            type: "SET_GRADE_DISTRIBUTION",
            payload: response,
          });
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsDistributionLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    gradingPanel.visible,
    gradingPanel.view,
    currentDocument?.id,
    currentDocument?.assessmentId,
    fetchGradeDistribution,
    gradeDistribution,
    dispatch,
  ]);

  const handleTabChange = (value: string) => {
    if (value === "remarks" || value === "distribution") {
      dispatch({
        type: "SHOW_GRADING_PANEL",
        payload: { visible: true, view: value },
      });
    }
  };

  const handleClose = () =>
    dispatch({ type: "SHOW_GRADING_PANEL", payload: { visible: false } });

  if (!currentDocument || currentView !== "grading" || !gradingPanel.visible) {
    return null;
  }

  const { title, description } = TITLE_COPY[gradingPanel.view];

  return (
    <Tabs value={gradingPanel.view} onValueChange={handleTabChange}>
      <PanelLayout
        title={title}
        description={description}
        onClose={handleClose}
        headerContent={
          <TabsList className="bg-transparent gap-1">
            <TabsTrigger
              value="remarks"
              className="px-2 data-[state=active]:bg-accent data-[state=active]:shadow-none cursor-pointer"
            >
              <MessageSquareText className="h-4 w-4" />
            </TabsTrigger>
            <TabsTrigger
              value="distribution"
              className="px-2 data-[state=active]:bg-accent data-[state=active]:shadow-none cursor-pointer"
            >
              <BarChart3 className="h-4 w-4" />
            </TabsTrigger>
          </TabsList>
        }
      >
        <div className="pt-2">
          <TabsContent value="remarks" className="mt-0">
            <UserRemarksTab
              document={currentDocument}
              selectedCriterionId={gradingPanel.selectedCriterionId}
            />
          </TabsContent>
          <TabsContent value="distribution" className="mt-0">
            <GradeDistributionTab
              data={gradeDistribution}
              isLoading={isDistributionLoading}
              currentGrade={grading?.grade}
            />
          </TabsContent>
        </div>
      </PanelLayout>
    </Tabs>
  );
};
